import { signal, computed, effect } from '@angular/core';

// Generic interfaces
export type SearchCriteria = Record<string, string | number | null | undefined>;

export interface SearchState<TResult, TCriteria extends SearchCriteria> {
    searchCriteria: TCriteria;
    searchResults: TResult[];
    loading: boolean;
    hasSearched: boolean;
}

// Configuration interface
export interface SearchStateConfig {
    storageKey: string;
    entityName: string; // For logging/debugging
}

export abstract class SearchStateService<
    TResult,
    TCriteria extends SearchCriteria
> {
    // Private signals
    private readonly _searchCriteria = signal<TCriteria>({} as TCriteria);
    private readonly _searchResults = signal<TResult[]>([]);
    private readonly _loading = signal<boolean>(false);
    private readonly _hasSearched = signal<boolean>(false);

    // Public readonly signals
    public readonly searchCriteria = this._searchCriteria.asReadonly();
    public readonly searchResults = this._searchResults.asReadonly();
    public readonly loading = this._loading.asReadonly();
    public readonly hasSearched = this._hasSearched.asReadonly();

    // Computed signals
    public readonly hasResults = computed(
        () => this._searchResults().length > 0
    );
    public readonly isEmpty = computed(
        () => this._searchResults().length === 0 && this._hasSearched()
    );

    protected config: SearchStateConfig;

    constructor(config: SearchStateConfig) {
        this.config = config;

        // Load state from sessionStorage on service initialization
        this.loadStateFromStorage();

        // Auto-save state changes to sessionStorage
        effect(() => {
            const state: SearchState<TResult, TCriteria> = {
                searchCriteria: this._searchCriteria(),
                searchResults: this._searchResults(),
                loading: this._loading(),
                hasSearched: this._hasSearched()
            };
            this.saveStateToStorage(state);
        });
    }

    // Actions
    public setSearchCriteria(criteria: TCriteria): void {
        this._searchCriteria.set(criteria);
    }

    public setSearchResults(results: TResult[]): void {
        this._searchResults.set(results);
        this._hasSearched.set(true);
    }

    public setLoading(loading: boolean): void {
        this._loading.set(loading);
    }

    public clearState(): void {
        this._searchCriteria.set({} as TCriteria);
        this._searchResults.set([]);
        this._loading.set(false);
        this._hasSearched.set(false);
        this.clearStorage();
    }

    public updateSearchCriteria(partial: Partial<TCriteria>): void {
        this._searchCriteria.update((current) => ({ ...current, ...partial }));
    }

    // Storage methods
    private saveStateToStorage(state: SearchState<TResult, TCriteria>): void {
        try {
            sessionStorage.setItem(
                this.config.storageKey,
                JSON.stringify(state)
            );
        } catch (error) {
            console.warn(
                `Failed to save ${this.config.entityName} search state to sessionStorage:`,
                error
            );
        }
    }

    private loadStateFromStorage(): void {
        try {
            const stored = sessionStorage.getItem(this.config.storageKey);
            if (stored) {
                const state: SearchState<TResult, TCriteria> =
                    JSON.parse(stored);
                this._searchCriteria.set(
                    state.searchCriteria || ({} as TCriteria)
                );
                this._searchResults.set(state.searchResults || []);
                this._loading.set(false); // Always reset loading to false on page load
                this._hasSearched.set(state.hasSearched || false);
            }
        } catch (error) {
            console.warn(
                `Failed to load ${this.config.entityName} search state from sessionStorage:`,
                error
            );
            this.clearState();
        }
    }

    private clearStorage(): void {
        try {
            sessionStorage.removeItem(this.config.storageKey);
        } catch (error) {
            console.warn(
                `Failed to clear ${this.config.entityName} search state from sessionStorage:`,
                error
            );
        }
    }
}
